/**
 * ID41 상품 커스텀 사용자 지정 폼 + 가변 동의 항목.
 *
 * be-catalog 가 event_collection_fields 패턴을 상품으로 포팅(task #1).
 * 필드 유형: 단답(TEXT)/장문(TEXTAREA)/라디오(RADIO)/체크박스(CHECKBOX)/드롭다운(SELECT).
 * SELECT/RADIO/CHECKBOX 만 options[] 사용. consent 는 상품별 가변 동의 N행.
 */
export const COLLECTION_FIELD_TYPES = [
  'TEXT',
  'TEXTAREA',
  'RADIO',
  'CHECKBOX',
  'SELECT',
] as const;
export type CollectionFieldType = (typeof COLLECTION_FIELD_TYPES)[number];

/** options[] 를 사용하는 선택형 유형. */
export const OPTION_FIELD_TYPES: ReadonlyArray<CollectionFieldType> = ['RADIO', 'CHECKBOX', 'SELECT'];

export const fieldTypeUsesOptions = (type: CollectionFieldType): boolean =>
  OPTION_FIELD_TYPES.includes(type);

export interface CollectionField {
  /** 안정 클라이언트 키 (저장 시 BE 가 영속 id 부여; 신규 행 식별용). */
  key: string;
  label: string;
  type: CollectionFieldType;
  required: boolean;
  /** RADIO/CHECKBOX/SELECT 일 때만 의미. 그 외는 빈 배열. */
  options: string[];
  /** 개인정보 민감 필드 여부(동의 필요). event_collection_fields 의 isSensitive 대응. */
  sensitive: boolean;
}

export interface ConsentItem {
  key: string;
  title: string;
  /** 약관 본문 텍스트 (content) — 링크만 쓸 경우 비울 수 있다. */
  content: string;
  /** 외부 약관 링크 (선택). */
  linkUrl: string;
  required: boolean;
}

/** GET/PUT 본문 — BE 와 1:1 (key 는 신규 행 only, 기존 행은 BE id 보존은 BE 책임). */
export interface ProductCollectionConfig {
  fields: CollectionField[];
  consents: ConsentItem[];
}

let seq = 0;
/** 신규 행 클라이언트 키 생성 (저장 전 React key + 추적용). */
export const newRowKey = (prefix: string): string => {
  seq += 1;
  return `${prefix}-${Date.now().toString(36)}-${seq}`;
};

export const emptyField = (): CollectionField => ({
  key: newRowKey('field'),
  label: '',
  type: 'TEXT',
  required: false,
  options: [],
  sensitive: false,
});

export const emptyConsent = (): ConsentItem => ({
  key: newRowKey('consent'),
  title: '',
  content: '',
  linkUrl: '',
  required: true,
});

/**
 * 저장 정규화: 라벨/타이틀 빈 행 제거, 선택형이 아닌 필드의 options 비움,
 * 선택형 필드의 빈 옵션 제거. 순수 함수 — 단위테스트 대상.
 */
export const normalizeCollectionConfig = (
  config: ProductCollectionConfig,
): ProductCollectionConfig => ({
  fields: config.fields
    .filter((f) => f.label.trim().length > 0)
    .map((f) => ({
      ...f,
      label: f.label.trim(),
      options: fieldTypeUsesOptions(f.type)
        ? f.options.map((o) => o.trim()).filter((o) => o.length > 0)
        : [],
    })),
  consents: config.consents
    .filter((c) => c.title.trim().length > 0)
    .map((c) => ({
      ...c,
      title: c.title.trim(),
      content: c.content.trim(),
      linkUrl: c.linkUrl.trim(),
    })),
});
